import { Form, Formik } from 'formik';
import React, { FC } from 'react';
import { bool, object, string } from 'yup';

// Components
import { FormikDropdown, FormikInput, FormikToggle } from '../../formik';
import { Button } from '../../Button';

// i18n
import { useTranslation } from '../../../translations/useTranslation';

// Models
import { ResumedMembershipFormValue } from '../../../models/Forms/ResumedMembershipFormValue';

// Utils
import { getCgiDropdownOptions } from '../../../utilities/cgi';

interface Props {
  formValues: ResumedMembershipFormValue;
  onSubmit: (values: ResumedMembershipFormValue) => Promise<void>;
  introductionText?: string;
  directDebitText?: string;
  signingText?: string;
}

const ResumedMembershipForm: FC<Props> = ({
  formValues,
  onSubmit,
  introductionText,
  directDebitText,
  signingText,
}) => {
  const { t, keys } = useTranslation();

  const signOptions = getCgiDropdownOptions(t, keys);

  const validationSchema = object().shape({
    fullName: string().required(t(keys.errorRequired)),
    email: string()
      .required(t(keys.errorRequired))
      .email(t(keys.errorInvalidEmail)),
    phone: string().required(t(keys.errorRequired)),
    address: string().required(t(keys.errorRequired)),
    city: string().required(t(keys.errorRequired)),
    zipCode: string().required(t(keys.errorRequired)),
    consent: bool().oneOf(
      [true],
      t(keys.resumedMemberShipFormConsentErrorMessage)
    ),
  });

  return (
    <div>
      <h2 className="form-app__heading">
        {t(keys.resumedMemberShipFormTitle)}
      </h2>
      {introductionText && (
        <div dangerouslySetInnerHTML={{ __html: introductionText }}></div>
      )}
      <Formik
        initialValues={formValues}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={onSubmit}
      >
        {({ isSubmitting, isValid, submitForm }) => (
          <Form>
            <fieldset className="aea-form-fieldset">
              <legend>{t(keys.uiPersonalInformation)}</legend>
              <FormikInput
                label={`${t(keys.formName)} *`}
                type="text"
                name="fullName"
                placeholder={t(keys.formFullName)}
              />
              <FormikInput
                label={`${t(keys.formEmailAddress)} *`}
                type="text"
                name="email"
              />
              <FormikInput
                label={`${t(keys.formPhoneNumber)} *`}
                type="text"
                name="phone"
              />
              <FormikInput
                label={`${t(keys.formStreetAddress)} *`}
                type="text"
                name="address"
              />
              <FormikInput
                label={`${t(keys.formPostalCode)} *`}
                type="text"
                name="zipCode"
              />
              <FormikInput
                label={`${t(keys.formCity)} *`}
                type="text"
                name="city"
              />
            </fieldset>

            <fieldset className="aea-form-fieldset">
              <legend>{t(keys.resumedMembershipFormDirectDebit)}</legend>
              {directDebitText && (
                <div
                  dangerouslySetInnerHTML={{ __html: directDebitText }}
                ></div>
              )}
              <FormikInput
                label={t(keys.resumedMemberShipFormBankInput)}
                type="text"
                name="directDebit.bank"
              />
              <FormikInput
                label={t(keys.resumedMemberShipFormClearingInput)}
                type="text"
                name="directDebit.clearingNumber"
              />
              <FormikInput
                label={t(keys.resumedMemberShipFormAccountInput)}
                type="text"
                name="directDebit.accountNumber"
              />
              <FormikToggle
                label={t(keys.resumedMemberShipFormDirectDebitConsent)}
                name="directDebit.consent"
              />
            </fieldset>

            <fieldset className="aea-form-fieldset">
              <legend>{t(keys.resumedMemberShipFormSign)}</legend>
              {signingText && (
                <div dangerouslySetInnerHTML={{ __html: signingText }}></div>
              )}
              <FormikDropdown
                name="authenticationOption"
                label={`${t(keys.formSelectAuthenticationOption)} *`}
                options={signOptions}
              />
              <FormikToggle
                label={t(keys.resumedMemberShipFormConsent)}
                name="consent"
              />
            </fieldset>
            <Button
              title={t(keys.resumedMemberShipFormSubmit)}
              disabled={!isValid}
              onClick={submitForm}
              isLoading={isSubmitting}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ResumedMembershipForm;
